/**
 * Usuarios de administración: quién entra a este backoffice.
 *
 * La lista NO sale de la tabla `users`, sale de los roles de Keycloak, que es
 * lo que de verdad concede el acceso. Por eso aparecen también los
 * administradores creados a mano en la consola de Keycloak — que hasta ahora
 * eran la única forma de darlos de alta.
 *
 * Los usuarios DE CUENTA (portal y contact center) son otra pantalla:
 * "Gestión de usuarios".
 */

import React, { createContext, useContext, useState } from "react";
import {
  FunctionField,
  List,
  TextField as RaTextField,
  useGetIdentity,
  useNotify,
  useRecordContext,
  useRefresh,
  useSidebarState,
  useTranslate,
} from "react-admin";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Edit as EditIcon,
  VpnKey as KeyIcon,
  BlockOutlined as BlockIcon,
  LockOpenOutlined as UnblockIcon,
  RemoveModeratorOutlined as RevokeIcon,
  MoreHoriz as MoreIcon,
} from "@mui/icons-material";
import {
  CustomDatagrid,
  FilterField,
  ListActionsBar,
} from "@/components/common";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import { useIsSuperUser } from "@/utils/permissions";
import {
  revokePlatformUserAccess,
  setPlatformUserEnabled,
} from "../services/platformUserService";
import type { PlatformUserRecord } from "../types/platformUser";
import PlatformUserFormDialog from "./PlatformUserFormDialog";
import PlatformUserPasswordDialog from "./PlatformUserPasswordDialog";

const i18nBase = "resources.platform-users";

interface RowActionContext {
  currentUserId: string;
  openEdit: (record: PlatformUserRecord) => void;
  openPassword: (record: PlatformUserRecord) => void;
  toggleEnabled: (record: PlatformUserRecord) => void;
  openRevoke: (record: PlatformUserRecord) => void;
}

const RowActionContext = createContext<RowActionContext | null>(null);
const useRowActions = (): RowActionContext => {
  const ctx = useContext(RowActionContext);
  if (!ctx) {
    throw new Error("Row action button used outside PlatformUserList");
  }
  return ctx;
};

const PlatformUserRowMenu: React.FC = () => {
  const record = useRecordContext<PlatformUserRecord>();
  const translate = useTranslate();
  const { currentUserId, openEdit, openPassword, toggleEnabled, openRevoke } =
    useRowActions();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  if (!record) return null;

  // Nadie se bloquea, degrada ni revoca a sí mismo: el backend lo rechaza y
  // aquí ni se ofrece, para no dejar a nadie fuera del admin por accidente.
  const isSelf = record.id === currentUserId;
  const close = (): void => setAnchorEl(null);
  const item = (
    icon: React.ReactNode,
    label: string,
    onClick: () => void,
    danger = false,
  ): React.ReactNode => (
    <MenuItem
      onClick={(e) => {
        e.stopPropagation();
        close();
        onClick();
      }}
      sx={danger ? { color: "error.main" } : undefined}
    >
      <ListItemIcon sx={{ color: "inherit" }}>{icon}</ListItemIcon>
      <ListItemText>{label}</ListItemText>
    </MenuItem>
  );

  return (
    <>
      <IconButton
        size="small"
        aria-label="row actions"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
      >
        <MoreIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
      >
        {item(<EditIcon sx={{ fontSize: 18 }} />, translate("ra.action.edit"), () =>
          openEdit(record),
        )}
        {/* Las cuentas de servicio no tienen contraseña: entran por client credentials. */}
        {!record.serviceAccount &&
          item(
            <KeyIcon sx={{ fontSize: 18 }} />,
            translate(`${i18nBase}.actions.changePassword`),
            () => openPassword(record),
          )}
        {!isSelf &&
          (record.enabled
            ? item(
                <BlockIcon sx={{ fontSize: 18 }} />,
                translate(`${i18nBase}.actions.block`),
                () => toggleEnabled(record),
              )
            : item(
                <UnblockIcon sx={{ fontSize: 18 }} />,
                translate(`${i18nBase}.actions.unblock`),
                () => toggleEnabled(record),
              ))}
        {!isSelf &&
          record.managedHere &&
          item(
            <RevokeIcon sx={{ fontSize: 18 }} />,
            translate(`${i18nBase}.actions.revoke`),
            () => openRevoke(record),
            true,
          )}
      </Menu>
    </>
  );
};

const ListActions: React.FC = () => {
  const translate = useTranslate();
  return (
    <ListActionsBar
      filters={
        <>
          <FilterField
            source="name"
            label={translate(`${i18nBase}.filters.name`)}
          />
          <FilterField
            source="surname"
            label={translate(`${i18nBase}.filters.surname`)}
          />
          <FilterField
            source="email"
            label={translate(`${i18nBase}.filters.email`)}
          />
        </>
      }
    />
  );
};

export const PlatformUserList: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const isSuperUser = useIsSuperUser();
  const [sidebarOpen] = useSidebarState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const maxWidth = isMobile ? "100%" : sidebarOpen ? "85vw" : "95vw";

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PlatformUserRecord | null>(null);
  const [passwordTarget, setPasswordTarget] =
    useState<PlatformUserRecord | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<PlatformUserRecord | null>(
    null,
  );
  const [rowBusy, setRowBusy] = useState(false);

  const toggleEnabled = async (record: PlatformUserRecord): Promise<void> => {
    if (rowBusy) return;
    setRowBusy(true);
    try {
      await setPlatformUserEnabled(record.id, !record.enabled);
      notify(
        `${i18nBase}.notifications.${record.enabled ? "blocked" : "unblocked"}`,
        { type: "success" },
      );
      refresh();
    } catch (err: unknown) {
      notify(
        extractErrorMessage(err, "ra.notification.http_error", translate),
        { type: "error" },
      );
    } finally {
      setRowBusy(false);
    }
  };

  const confirmRevoke = async (): Promise<void> => {
    if (!revokeTarget || rowBusy) return;
    setRowBusy(true);
    try {
      await revokePlatformUserAccess(revokeTarget.id);
      notify(`${i18nBase}.notifications.revoked`, { type: "success" });
      setRevokeTarget(null);
      refresh();
    } catch (err: unknown) {
      notify(
        extractErrorMessage(err, "ra.notification.http_error", translate),
        { type: "error" },
      );
    } finally {
      setRowBusy(false);
    }
  };

  const handlers: RowActionContext = {
    currentUserId: identity?.id ? String(identity.id) : "",
    openEdit: (record) => {
      setEditing(record);
      setFormOpen(true);
    },
    openPassword: setPasswordTarget,
    toggleEnabled,
    openRevoke: setRevokeTarget,
  };

  if (!isSuperUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
          {translate(`${i18nBase}.help.superUserOnly`)}
        </Typography>
      </Box>
    );
  }

  return (
    <RowActionContext.Provider value={handlers}>
      <Box>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: "text.primary", mb: 1, mt: 0, ml: 2 }}
        >
          {translate(`${i18nBase}.name`, { smart_count: 2 })}
          <Button
            variant="contained"
            size="small"
            sx={{ ml: 2, verticalAlign: "middle" }}
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            {translate(`${i18nBase}.actions.addUser`)}
          </Button>
        </Typography>
        <Typography
          sx={{ fontSize: 12.5, color: "text.secondary", mb: 2, ml: 2, maxWidth: 760 }}
        >
          {translate(`${i18nBase}.help.intro`)}
        </Typography>

        <List
          actions={<ListActions />}
          sort={{ field: "0", order: "ASC" }}
          storeKey={false}
          disableSyncWithLocation
          perPage={10}
          exporter={false}
          sx={{
            maxWidth,
            overflow: "visible",
            "& .RaList-main, & .RaList-content": {
              overflow: "visible",
              width: "100%",
              maxWidth: "100%",
            },
            "& .RaListToolbar-root, & .RaList-actions": {
              display: "flex",
              width: "100%",
              mb: { xs: 1, sm: 2 },
              px: 0,
              py: { xs: 0, sm: 1 },
              minHeight: "auto",
            },
          }}
        >
          <CustomDatagrid
            primarySource="email"
            bulkActionButtons={false}
            disableMobileView
          >
            <FunctionField
              label={translate(`${i18nBase}.columns.actions`)}
              render={() => <PlatformUserRowMenu />}
            />
            <RaTextField source="name" />
            <RaTextField source="surname" />
            <RaTextField source="email" />
            <FunctionField
              source="roleName"
              label={translate(`${i18nBase}.fields.role`)}
              render={(record: PlatformUserRecord) => (
                <Chip
                  size="small"
                  color={record.roleName === "SuperUser" ? "primary" : "default"}
                  variant={record.roleName === "SuperUser" ? "filled" : "outlined"}
                  label={translate(`${i18nBase}.roles.${record.roleName}`)}
                  sx={{ height: 22, fontSize: 11.5 }}
                />
              )}
            />
            <FunctionField
              source="enabled"
              label={translate(`${i18nBase}.fields.status`)}
              render={(record: PlatformUserRecord) =>
                record.enabled ? (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label={translate(`${i18nBase}.status.active`)}
                  />
                ) : (
                  <Chip
                    size="small"
                    color="warning"
                    label={translate(`${i18nBase}.status.blocked`)}
                  />
                )
              }
            />
            <FunctionField
              source="managedHere"
              label={translate(`${i18nBase}.fields.origin`)}
              render={(record: PlatformUserRecord) => (
                <Box sx={{ display: "inline-flex", gap: 0.5, flexWrap: "wrap" }}>
                  {!record.managedHere && (
                    <Tooltip title={translate(`${i18nBase}.badges.realmHelp`)}>
                      <Chip
                        size="small"
                        color="info"
                        variant="outlined"
                        label={translate(`${i18nBase}.badges.realm`)}
                        sx={{ height: 20, fontSize: 11, cursor: "default" }}
                      />
                    </Tooltip>
                  )}
                  {record.serviceAccount && (
                    <Tooltip title={translate(`${i18nBase}.badges.serviceHelp`)}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={translate(`${i18nBase}.badges.service`)}
                        sx={{ height: 20, fontSize: 11, cursor: "default" }}
                      />
                    </Tooltip>
                  )}
                  {!record.hasLocalProfile && (
                    <Tooltip title={translate(`${i18nBase}.badges.noProfileHelp`)}>
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={translate(`${i18nBase}.badges.noProfile`)}
                        sx={{ height: 20, fontSize: 11, cursor: "default" }}
                      />
                    </Tooltip>
                  )}
                </Box>
              )}
            />
          </CustomDatagrid>
        </List>

        <PlatformUserFormDialog
          record={editing}
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSuccess={() => {
            setFormOpen(false);
            refresh();
          }}
        />
        <PlatformUserPasswordDialog
          record={passwordTarget}
          onClose={() => setPasswordTarget(null)}
          onSuccess={() => setPasswordTarget(null)}
        />

        <Dialog
          open={revokeTarget !== null}
          onClose={() => !rowBusy && setRevokeTarget(null)}
          PaperProps={{ sx: { width: 460, maxWidth: "90vw" } }}
        >
          <DialogContent sx={{ textAlign: "center", pt: "24px !important" }}>
            <Typography sx={{ fontSize: "14.5px", fontWeight: 700, mb: 1 }}>
              {translate(`${i18nBase}.revoke.title`, {
                email: revokeTarget?.email ?? "",
              })}
            </Typography>
            <Typography
              sx={{ fontSize: "12.5px", color: "text.secondary", lineHeight: 1.55 }}
            >
              {translate(`${i18nBase}.revoke.content`)}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center" }}>
            <Button disabled={rowBusy} onClick={() => setRevokeTarget(null)}>
              {translate("ra.action.cancel")}
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={rowBusy}
              onClick={() => void confirmRevoke()}
            >
              {translate(`${i18nBase}.actions.revokeConfirm`)}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </RowActionContext.Provider>
  );
};

export default PlatformUserList;
